import { useMemo, useState } from 'react';

import { Button, ExcelIcon, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useModal, useStableCallback } from '~/hooks/common';
import { useGetCollectingResultReportDetailMutation } from '~/hooks/delivery-agency/queries';
import { getDeliveryAgencyCollectingResultReportsColumnDefs } from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY_PAGE_SIZE } from '~/constants/delivery-agency';

import CollectingResultReportDetailModal from '../CollectingResultReportDetailModal';

import type { TypePagination } from '~/types/common.types';
import type { CollectingResultReportDetail, CollectingResultReportItem } from '~/types/delivery-agency.types';

import styles from './CollectingResultReportTable.module.scss';

const CollectingResultReportTable = ({
  data,
  pagination,
  onChangePageLimit,
  onChangePage,
  onClickExcelDownload,
}: {
  data: CollectingResultReportItem[] | null;
  pagination: TypePagination;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onClickExcelDownload?: () => void;
}) => {
  const detailModal = useModal();
  const [detailData, setDetailData] = useState<CollectingResultReportDetail | null>(null);

  const reportDetailMutation = useGetCollectingResultReportDetailMutation({ showGlobalDimOverlay: false });

  const handleClickViewDetail = useStableCallback((report: CollectingResultReportItem) => {
    reportDetailMutation.mutate(
      { reportId: report.id },
      {
        onSuccess: (detail) => {
          setDetailData(detail);
          detailModal.handleOpenModal();
        },
      }
    );
  });

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyCollectingResultReportsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: handleClickViewDetail,
      }),
    [pagination.currentPage, pagination.pageSize, handleClickViewDetail]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <Button
            size="tiny"
            variant="outlined"
            color="tertiary"
            disabled={!onClickExcelDownload || pagination.totalCount === 0}
            leftIcon={<ExcelIcon size={16} />}
            onClick={onClickExcelDownload}
          >
            리포트 전체 다운로드
          </Button>
        }
      />

      <div className={styles['table-wrapper']}>
        <Table data={data} columns={columnDefs} rowStatusType={data === null ? 'INIT' : 'COLLECTING_REQUEST_EMPTY'} />
      </div>

      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
      />

      {detailModal.isOpen && detailData && (
        <CollectingResultReportDetailModal
          open={detailModal.isOpen}
          data={detailData}
          onClose={detailModal.handleCloseModal}
        />
      )}
    </div>
  );
};

export default CollectingResultReportTable;
